import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"

// Drop target on a Planning::TradeDemandChipComponent. Accepts a dragged employee and
// fulfils one slot of the trade demand. The server allows non-matching trades — the chip
// surfaces the mismatch with a yellow ring and tooltip. Drops that don't carry an employee
// payload (e.g. a Trade drag from the panel) bubble up to the board's planning-board-drop
// handler, so the Trade drag still creates a new demand when dropped on the chip area.
export default class extends Controller {
  static values = { url: String }

  connect() {
    this.dragCounter = 0
  }

  dragover(event) {
    // Only show drop affordance for employee drags. The chip is also covered by the
    // outer cell, so the parent handles trade drops; this controller filters them out.
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  dragenter(event) {
    event.preventDefault()
    this.dragCounter++
    this.element.classList.add("ring-2", "ring-emerald-400")
  }

  dragleave(_event) {
    this.dragCounter--
    if (this.dragCounter <= 0) {
      this.dragCounter = 0
      this.element.classList.remove("ring-2", "ring-emerald-400")
    }
  }

  drop(event) {
    this.dragCounter = 0
    this.element.classList.remove("ring-2", "ring-emerald-400")

    try {
      const payload = event.dataTransfer.getData("application/json")
      if (!payload) return

      const data = JSON.parse(payload)
      if (data.resourceType !== "Employee" || !data.resourceId) return

      // Only stop propagation for employee drops; Trade drops fall through to the day cell.
      event.preventDefault()
      event.stopPropagation()
      this.fulfill(data.resourceId)
    } catch (error) {
      Honeybadger.notify(error, {
        context: { component: "planning-trade-fill", action: "drop" },
      })
    }
  }

  fulfill(employeeId) {
    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    fetch(this.urlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ employee_id: employeeId }),
    })
      .then(async (response) => {
        const html = await response.text()
        const contentType = response.headers.get("Content-Type") || ""
        if (contentType.includes("text/vnd.turbo-stream.html")) {
          Turbo.renderStreamMessage(html)
        } else if (!response.ok) {
          Honeybadger.notify(`Failed to fulfill demand: ${response.status}`, {
            context: { level: "error", component: "planning-trade-fill" },
          })
        }
      })
      .catch((error) => {
        Honeybadger.notify(error, {
          context: { component: "planning-trade-fill", action: "fulfill" },
        })
      })
  }
}
